import React, { useState, useEffect } from 'react';
import { User, MapPin, Clock, Users, ShieldCheck, Heart, Info, Star } from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA GLOBAL
import { useGlobal } from 'context/GlobalContext';

const MiSalon = ({ ninos }) => {
  // Consumimos los datos reales (salones y maestros asignados)
  const { ninos: ninosGlobales } = useGlobal();
  const misNinosReal = ninos || ninosGlobales;

  // Inicializar con el primer niño de la familia
  const [ninoActivo, setNinoActivo] = useState(misNinosReal[0] || null);

  useEffect(() => {
    if (!ninoActivo && misNinosReal.length > 0) {
      setNinoActivo(misNinosReal[0]);
    }
  }, [misNinosReal, ninoActivo]);

  if (!ninoActivo) return (
    <div className="p-20 text-center opacity-30 font-black uppercase tracking-widest italic">
      No hay información de salones disponible
    </div>
  );

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-10 animate-in fade-in duration-500 pb-20 px-4 font-sans">
      
      {/* HEADER DINÁMICO */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b-4 border-slate-100 pb-8 text-left">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-full border border-indigo-100 shadow-sm">
            <Star size={16} fill="currentColor" />
            <span className="text-[10px] font-black uppercase tracking-widest italic">Información de Aula</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 italic tracking-tighter leading-none uppercase">Mi Salón</h2>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em]">Espacio verificado y equipo a cargo</p>
        </div>

        {/* SELECTOR DE NIÑOS */}
        <div className="flex bg-white p-2 rounded-[2.5rem] shadow-xl border border-slate-100 gap-2 overflow-x-auto hide-scrollbar">
          {misNinosReal.map(nino => (
            <button
              key={nino.id}
              onClick={() => setNinoActivo(nino)}
              className={`flex items-center gap-3 px-6 py-4 rounded-[1.8rem] whitespace-nowrap transition-all duration-300 ${
                ninoActivo.id === nino.id 
                ? 'bg-slate-900 text-white shadow-lg' 
                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${ninoActivo.id === nino.id ? 'bg-amber-500' : 'bg-slate-200'}`}>
                <User size={16} className={ninoActivo.id === nino.id ? 'text-slate-900' : 'text-slate-400'} />
              </div>
              <span className="text-xs font-black uppercase tracking-widest italic">{nino.nombre.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* LADO IZQUIERDO: DETALLES DEL GRUPO */}
        <div className="lg:col-span-5 space-y-6 text-left">
          <div className="bg-white p-10 rounded-[4rem] shadow-sm border-2 border-slate-50 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-50 rounded-bl-[5rem] -mr-12 -mt-12 transition-transform group-hover:scale-110" />
            
            <div className="relative z-10 space-y-8">
              <div className="space-y-2">
                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-500 flex items-center gap-2 italic">
                  <MapPin size={14} /> Ubicación en Tiempo Real
                </h3>
                <h4 className="text-4xl font-black text-slate-900 tracking-tighter leading-tight uppercase italic">
                  {ninoActivo.salon || 'ASIGNANDO...'}
                </h4>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-5 p-6 bg-slate-50 rounded-[2.5rem] border border-slate-100 shadow-inner">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-indigo-400 shadow-sm">
                    <MapPin size={24} />
                  </div>
                  <div className="text-left">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 italic">Instalación Asignada</p>
                    <p className="text-lg font-black text-slate-800 uppercase italic leading-none">{ninoActivo.ubicacion || 'Área Principal'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 p-6 bg-slate-50 rounded-[2.5rem] border border-slate-100 shadow-inner">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-indigo-400 shadow-sm">
                    <Clock size={24} />
                  </div>
                  <div className="text-left">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 italic">Horario de Operación</p>
                    <p className="text-lg font-black text-slate-800 uppercase italic leading-none">{ninoActivo.horario || '10:00 AM - 12:30 PM'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 p-6 rounded-3xl border-2 border-amber-100 flex gap-4 text-left">
                <Info className="text-amber-500 shrink-0" size={20} />
                <p className="text-[11px] font-bold text-amber-900 leading-relaxed italic">
                  Recoge a {ninoActivo.nombre.split(' ')[0]} puntualmente presentando tu carnet digital para facilitar la seguridad de todos los exploradores.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* LADO DERECHO: STAFF A CARGO */}
        <div className="lg:col-span-7 space-y-6 text-left">
          <h4 className="flex items-center gap-3 font-black text-slate-900 uppercase text-[10px] tracking-[0.4em] ml-6 italic">
            <Users size={20} className="text-indigo-500" /> Personal del Salón
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(ninoActivo.maestros || [{id: 1, nombre: 'Staff Casa de Fe', rol: 'Facilitador'}]).map((maestro) => (
              <div key={maestro.id} className="bg-white p-8 rounded-[3.5rem] border-2 border-slate-50 flex flex-col items-center text-center space-y-6 hover:shadow-xl transition-all duration-500 group">
                <div className="w-24 h-24 rounded-[2.5rem] bg-indigo-50 text-indigo-500 flex items-center justify-center relative overflow-hidden transition-transform group-hover:scale-110 shadow-inner">
                  <Heart size={40} fill="currentColor" className="opacity-10 absolute" />
                  <User size={40} className="relative z-10" strokeWidth={1.5} />
                </div>
                
                <div>
                  <span className="text-[9px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full inline-block mb-3 border border-indigo-100 italic">
                    {maestro.rol}
                  </span>
                  <p className="text-2xl font-black text-slate-800 leading-none tracking-tight uppercase italic">
                    {maestro.nombre}
                  </p>
                </div>

                <div className="w-full pt-4 border-t border-slate-50">
                  <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest italic">
                    Staff Certificado
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* BANNER DE SEGURIDAD */}
          <div className="bg-[#0A2440] p-8 rounded-[3.5rem] text-white flex items-center justify-between shadow-2xl relative overflow-hidden border-b-8 border-indigo-600">
            <div className="flex items-center gap-5 relative z-10 text-left">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20">
                <ShieldCheck className="text-emerald-400" size={32} />
              </div>
              <div>
                <p className="text-sm font-black uppercase tracking-widest leading-none italic">Ambiente Seguro</p>
                <p className="text-[9px] text-slate-400 font-bold mt-2 uppercase tracking-[0.2em]">Monitoreo constante por Casa de Fe</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MiSalon;